import { ApiResponseWithPaging, QueryResult } from '@dispatch/shared/types';
import { useState, useMemo } from 'react';
import { QueryKey, useInfiniteQuery } from 'react-query';
import { mapQueryStatus } from '../useQuery';
import { isNull, isUndefined } from 'lodash';

type QueryFnWithContinuationToken<TData> = (
  continuationToken?: string
) => Promise<ApiResponseWithPaging<TData>>;

export type PaginatedQueryResult<TData, TError> = QueryResult<TData, TError> & {
  getNextPage: () => void;
  getPreviousPage: () => void;
  goToFirstPage: () => void;
  currentPageNumber: number;
  hasNextPage: boolean;
  currentTotal: number;
  fetchCurrentPage: () => void;
  isFetching: boolean;
  pages: ApiResponseWithPaging<TData>[] | undefined;
};

export function usePaginatedQuery<TData, TQueryKey extends QueryKey = QueryKey>(
  queryKey: TQueryKey,
  queryFn: QueryFnWithContinuationToken<Array<TData>>,
  options?: any
): PaginatedQueryResult<Array<TData>, unknown> {
  const [currentPageIndex, setPage] = useState(0);
  const res = useInfiniteQuery(
    queryKey,
    ({ pageParam: continuationToken }) => queryFn(continuationToken),
    {
      getNextPageParam: (lastPage, _pages) => lastPage.continuationToken,
      cacheTime: 1000 * 5, // 5 seconds
      staleTime: 30000,
      keepPreviousData: true, // Keep old data visible while fetching new data
      ...options,
    }
  );

  const currentPage: ApiResponseWithPaging<Array<TData>> | undefined = useMemo(
    () => res?.data?.pages?.[currentPageIndex],
    [currentPageIndex, res?.data]
  );

  const currentTotal: number = useMemo(() => {
    return (
      res?.data?.pages?.reduce((acc, page) => {
        return acc + (page?.data?.length || 0);
      }, 0) || 0
    );
  }, [res?.data?.pages]);

  const hasNextPage = useMemo(() => {
    const token = currentPage?.continuationToken;

    if (!token) return false; // no token => no next page

    try {
      const parsed = JSON.parse(token);
      // If the extended object has ContinuationToken field:
      if (!isUndefined(parsed?.ContinuationToken)) {
        return !isNull(parsed.ContinuationToken);
      }
      // Fallback: any non-empty token means there’s a next page
      return true;
    } catch {
      // Token is a plain string; any non-empty value => next page exists
      return true;
    }
  }, [currentPage?.continuationToken]);

  return {
    fetchCurrentPage() {
      res.refetch({
        refetchPage: (_, index) => index === currentPageIndex,
      });
    },
    data: currentPage?.data,
    pages: res?.data?.pages,
    error: res.error,
    status: mapQueryStatus(
      currentPage?.data,
      res.status,
      currentPage?.hasFilters || false,
      res?.isFetchingNextPage,
      res?.isFetchingPreviousPage
    ),
    getNextPage() {
      setPage(currentPageIndex + 1);
      res.fetchNextPage();
    },
    getPreviousPage() {
      setPage(currentPageIndex - 1);
      res.fetchPreviousPage();
    },
    goToFirstPage() {
      setPage(0);
    },
    currentPageNumber: currentPageIndex,
    hasNextPage,
    currentTotal,
    isFetching: res.isFetching,
  };
}
